import TableHeaderWithTab from "@/components/common/table/header-with-tab";
import Pagination from "@/components/common/table/pagination";
import { FiltersModal } from "@/components/ui/filters/FiltersModal";
import { useAuth } from "@/contexts/auth-provider/useAuth";
import { useAuthorization } from "@/hooks/use-authorization";
import { useGetFormalityRequests } from "@/queries/formality-request";
import { useGetMe } from "@/queries/me";
import { useDocumentMetadataStore } from "@/stores/documents/useDocumentMetadataStore";
import {
  type FilterHierarchyStatusType,
  type FilterOption,
} from "@/types/common/filters";
import type { FormalityRequestType } from "@/types/entities/formality-request";
import { Permissions } from "@/utils/permissions";
import { Heading, TableData } from "@elaniintc/goes-design-system";
import type { SortingState } from "@tanstack/react-table";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useDebounceValue } from "usehooks-ts";
import { RequestAssigneeModal } from "../../components/ui/RequestAssigneeModal";
import { buildHierarchyStatusString } from "./utils/serialization";
import { useMetricsPermissions } from "./hooks/use-metrics-perms";

// Re-export FRSTATUS_BADGES for backward compatibility and constants access
export { FRSTATUS_BADGES } from "./constants";
import { createColumns } from "./request-management-columns";
import { defaultFilters } from "./utils/filters";
import NoData from "@/components/ui/no-data";

const RequestManagementTable = () => {
  const { data: me } = useGetMe();
  const { getUserRoleType } = useAuth();
  const userRoleType = getUserRoleType();
  const navigate = useNavigate();
  const { hasActivityBlock } = useMetricsPermissions();

  const isUserLeaderInSomeGroup = useMemo(() => {
    if (!me) return false;
    return me.data.groups.some(
      (group) => group.pivot.seniority === 1 || group.pivot.seniority === 2,
    );
  }, [me]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [query, setQuery] = useQueryState("search", parseAsString);
  const [searchInput, setSearchInput] = useState<string | null>(query || "");
  const [page, setPage] = useQueryState("page", parseAsInteger);
  const [deferredQuery] = useDebounceValue(searchInput || "", 500);
  const [order_by, setOrderBy] = useQueryState(
    "order_by",
    parseAsString.withDefault("!updated_at"),
  );
  const { hasPermission } = useAuthorization();
  const hasSubmissions = hasPermission(Permissions.widgets.submissions);
  const canReadRequests = hasPermission(Permissions.formality_requests.read);
  const [groupsIdToFind, setGroupsIdToFind] = useState<string[]>([]);
  const [status, setStatus] = useQueryState(
    "status",
    parseAsString.withDefault(""),
  );
  const [suspensionStatus, setSuspensionStatus] = useQueryState(
    "suspensionStatus",
    parseAsString.withDefault(""),
  );
  const [created_at, setCreatedAt] = useQueryState(
    "created_at",
    parseAsString.withDefault(""),
  );

  const [formatFilters, setFormatFilters] = useState<FilterOption[]>(
    defaultFilters({
      status,
      created_at,
      suspensionStatus,
    }),
  );

  useEffect(() => {
    setFormatFilters(
      defaultFilters({
        status,
        created_at,
        suspensionStatus,
      }),
    );
  }, [status, created_at, suspensionStatus]);

  const [activeTab, setActiveTab] = useState<string>("all");

  useEffect(() => {
    setQuery(deferredQuery || null);
    setPage(1);
  }, [deferredQuery, setQuery, setPage]);

  // Reset page when tab changes
  useEffect(() => {
    setPage(1);
  }, [activeTab, setPage]);

  const { data, isLoading } = useGetFormalityRequests(
    {
      ...(deferredQuery ? { "filter[search]": deferredQuery.trim() } : {}),
      page: page || 1,
      per_page: 20,
      ...(order_by ? { order_by: order_by } : {}),
      ...(status ? { "filter[node_status_pairs]": status } : {}),
      ...(created_at ? { "filter[created_at]": created_at } : {}),
      ...(me?.data?.id && activeTab === "my-signatures"
        ? { "filter[signable_by]": me.data.id.toString() }
        : {}),
      ...(suspensionStatus
        ? { "filter[suspension_status]": suspensionStatus }
        : {}),
      ...(activeTab === "express"
        ? { "filter[is_express]": "1" }
        : { "filter[is_express]": "0" }),
    },
    5000,
    !!me?.data && canReadRequests,
  );

  const groupsImLeaderOf = useMemo(() => {
    if (!me?.data?.groups?.length) return [];

    const leaderGroups = me.data.groups.filter(
      (group) => group?.pivot?.seniority === 1 || group?.pivot?.seniority === 2,
    );

    if (leaderGroups.length === 0) return [];

    return leaderGroups.map((g) => g.id);
  }, [me?.data?.groups]);

  const [modalState, setModalState] = useState<
    "idle" | "filters" | "userSelection"
  >("idle");

  const [selectedRequest, setSelectedRequest] =
    useState<FormalityRequestType | null>(null);

  const { setDocuments } = useDocumentMetadataStore();

  // Extract and store document metadata when data is fetched
  useEffect(() => {
    if (!data) return;

    // Handle both possible response structures: { data: [...] } or direct array
    const dataArray = Array.isArray(data) ? data : data?.data || [];

    if (Array.isArray(dataArray) && dataArray.length > 0) {
      const allDocuments = dataArray
        .filter((request: FormalityRequestType) => request.documents)
        .flatMap((request: FormalityRequestType) =>
          (request.documents || []).map((doc) => ({
            ...doc,
            node_id: doc.node_id,
            field_id: doc.field_id,
            field_name: doc.field_name,
            field_label: doc.field_label,
          })),
        );
      setDocuments(allDocuments);
    }
  }, [data, setDocuments]);

  const columns = createColumns(
    hasActivityBlock,
    setModalState,
    (request: FormalityRequestType) => {
      setSelectedRequest(request);
    },
    setGroupsIdToFind,
    (id: string) => navigate(`/tramite/${id}`),
    userRoleType === "admin" || isUserLeaderInSomeGroup, // Show assigned column only for admin and group leaders
    groupsImLeaderOf,
  );

  // All filtering is now handled server-side by the API
  const filteredData = useMemo(() => {
    // Check if data exists and has the expected structure
    if (!data) return [];

    // Handle both possible response structures: { data: [...] } or direct array
    const dataArray = Array.isArray(data) ? data : data?.data || [];

    if (!Array.isArray(dataArray) || dataArray.length === 0) {
      return [];
    }

    return dataArray;
  }, [data]);

  // Calculate pagination - now both tabs use server-side pagination
  const filteredPagination = useMemo(() => {
    if (!data || Array.isArray(data)) {
      return {
        currentPage: 1,
        totalItems: filteredData.length,
        totalPages: 1,
        pageSize: filteredData.length,
        hasNext: false,
        hasPrevious: false,
      };
    }

    // Use server-side pagination for both tabs
    return {
      currentPage: data?.meta?.current_page || 1,
      totalItems: data?.meta?.total || 0,
      totalPages: data?.meta?.last_page || 0,
      pageSize: Array.isArray(data?.data)
        ? data.data.length
        : data?.data?.length || 0,
      hasNext: (data?.meta?.current_page || 1) < (data?.meta?.last_page || 1),
      hasPrevious: (data?.meta?.current_page || 1) > 1,
    };
  }, [filteredData, data]);

  const onCloseFilters = (type: string) => {
    if (type !== "clean") {
      setModalState("idle");
      return;
    }
    setFormatFilters(
      defaultFilters({
        status: "",
        created_at: "",
      }),
    );
    setStatus("");
    setCreatedAt(null);
    setSuspensionStatus(null);
    setModalState("idle");
  };

  const onApplyFilters = (newFilters: FilterOption[]): FilterOption[] => {
    newFilters.forEach((f) => {
      if (f.isUpdated) {
        if (f.field === "status") {
          setStatus(buildHierarchyStatusString(f as FilterHierarchyStatusType));
        } else if (f.field === "created_at") {
          setCreatedAt(f.result);
        } else if (f.field === "suspensionStatus") {
          setSuspensionStatus(f.result);
        }
        setPage(1);
      }
    });

    // Set filter to updated false if result is empty
    const updatedFilters = newFilters.map((f) => {
      if (f.result === "") {
        return {
          ...f,
          isUpdated: false,
          result: "",
        };
      }
      return f;
    });
    return updatedFilters;
  };

  const handleCloseUserSelection = () => {
    setModalState("idle");
    setSelectedRequest(null);
    setGroupsIdToFind([]);
  };

  const hasActiveFiltersOrSearch = useMemo(() => {
    const hasSearchQuery = !!deferredQuery?.trim();
    const hasActiveFilters = formatFilters.some((filter) => filter.isUpdated);
    return hasSearchQuery || hasActiveFilters;
  }, [deferredQuery, formatFilters]);

  const tableContent = useMemo(() => {
    if (filteredData.length > 0) {
      return (
        <>
          <TableData
            columns={columns}
            data={filteredData}
            sorting={sorting}
            onSortingChange={(val) => {
              setSorting(val);
              if (val.length === 0) {
                setOrderBy("!updated_at");
                return;
              }
              setOrderBy(
                `${val[0]?.desc ? "!" : ""}${val[0].id === "elapsed_days" ? "created_at" : val[0].id}`,
              );
            }}
            stripped
            isFirstLoading={!data}
            wrapperClassName="min-h-125"
          />
          <div className="p-3">
            <Pagination
              currentPage={filteredPagination.currentPage}
              totalItems={filteredPagination.totalItems}
              hasNext={filteredPagination.hasNext}
              hasPrevious={filteredPagination.hasPrevious}
              onPageChange={(page) => {
                setPage(page);
              }}
              onNext={() => {
                setPage((data?.meta?.current_page || 1) + 1);
              }}
              onPrevious={() => {
                setPage((data?.meta?.current_page || 1) - 1);
              }}
              pageSize={filteredPagination.pageSize}
              totalPages={filteredPagination.totalPages}
            />
          </div>
        </>
      );
    }

    if (!isLoading && data) {
      return (
        <div className="relative border-y border-neutral-quiet w-full h-100 overflow-x-auto text-body flex flex-col justify-center items-center bg-neutral-object-accent-silent rounded-b-lg shadow-none">
          <Heading variant="h6" className="text-on-disabled">
            {hasActiveFiltersOrSearch
              ? "Sin entradas encontradas"
              : "Sin entradas creadas"}
          </Heading>
          <span className="text-sm text-on-disabled">
            {hasActiveFiltersOrSearch
              ? "No existen entradas que coincidan con los filtros o la búsqueda aplicada."
              : "Crea una nueva entrada para poder ver sus detalles aquí."}
          </span>
        </div>
      );
    }

    return (
      <TableData
        columns={columns}
        data={filteredData}
        sorting={sorting}
        onSortingChange={(val) => {
          setSorting(val);
          if (val.length === 0) {
            setOrderBy("!updated_at");
            return;
          }
          setOrderBy(
            `${val[0]?.desc ? "!" : ""}${val[0].id === "elapsed_days" ? "created_at" : val[0].id}`,
          );
        }}
        stripped
        isFirstLoading={!data}
        emptyState={<NoData title="No data" description="No data" />}
      />
    );
  }, [
    filteredData,
    columns,
    sorting,
    setSorting,
    setOrderBy,
    data,
    isLoading,
    filteredPagination,
    activeTab,
    setPage,
    hasActiveFiltersOrSearch,
  ]);

  return (
    <div
      data-testid="request-management-table"
      className="bg-neutral-object border-neutral border-[0.5px] rounded-xl shadow-custom overflow-hidden"
    >
      {modalState === "filters" && (
        <FiltersModal
          isOpen={modalState === "filters"}
          onToggle={onCloseFilters}
          hasCurrentFilters={formatFilters.some((filter) => filter.isUpdated)}
          items={formatFilters}
          onApply={(newFilters) => {
            const filters = onApplyFilters(newFilters);
            setFormatFilters(filters);
            setModalState("idle");
          }}
        />
      )}
      <TableHeaderWithTab
        tabs={[
          { label: "Normales", value: "all" },
          { label: "Express", value: "express" },
          { label: "Mis firmas", value: "my-signatures" },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        createLabel=""
        search={searchInput}
        placeholder="Buscar solicitud..."
        setSearch={setSearchInput}
        isLoading={isLoading}
        canCreate={false}
        onCreate={() => {}}
        onFilter={() => setModalState("filters")}
        filtersApplied={formatFilters.filter((f) => f.isUpdated).length}
      />
      {hasSubmissions && canReadRequests ? (
        <>
          {tableContent}
          {modalState === "userSelection" && (
            <RequestAssigneeModal
              isOpen={modalState === "userSelection"}
              onClose={handleCloseUserSelection}
              selectedRequest={selectedRequest}
              groupsImLeaderOf={groupsIdToFind}
            />
          )}
        </>
      ) : (
        <div
          data-testid="request-management-table-fallback"
          className="relative border-y border-neutral-quiet w-full h-64 overflow-x-auto text-body flex justify-center items-center gap-4 bg-neutral-object-accent-silent rounded-b-lg shadow-none"
        >
          <span className="text-center font-normal text-on-disabled-quiet">
            Sin permiso para ver estos datos
          </span>
        </div>
      )}
    </div>
  );
};

export default RequestManagementTable;
